import { documentApi } from "@api/documents";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import { DocumentListEntry } from "@modules/Document/backoffice/DocumentList";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import { BackOfficeRoutes } from "@routes/urls";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
} from "react-searchkit";
import { Container, Grid, Header, Button, Popup } from "semantic-ui-react";
import { Link } from "react-router-dom";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import { SearchDateRange } from "@pages/backoffice/Loan/LoanSearch/SearchDateRange";
import Qs from "qs";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import { WeedingThresholdButton } from "@components/backoffice/buttons/WeedingThresholdButton";
import MARC21ImportUploader from "@components/MARC21ImportUploader/MARC21ImportUploader";
import ExcelImportUploader from "@components/ExcelImportUploader/ExcelImportUploader";

export class DocumentSearch extends Component {
  modelName = "DOCUMENTS";

  searchApi = new InvenioSearchApi({
    axios: { url: documentApi.searchBaseURL, withCredentials: true },
  });

  getRequestSerializer = () => {
    const requestSerializer = this.searchApi.requestSerializer;
    if (
      !requestSerializer ||
      typeof requestSerializer.serialize !== "function"
    ) {
      throw new Error(
        "requestSerializer is not configured correctly in DocumentSearch."
      );
    }
    return requestSerializer;
  };

  handleExportClick = async (format, size, currentQueryState) => {
    // Handle different export formats
    switch (format) {
      case "pdf":
        return this.customExportPDF(currentQueryState, size);
      case "marcxml":
        return this.handleMARC21Export(currentQueryState, size, "marcxml");
      case "marc21":
        return this.handleMARC21Export(currentQueryState, size, "marc21");
      default:
        return this.handleStandardExport(currentQueryState, size, format);
    }
  };

  handleStandardExport = async (currentQueryState, size, format) => {
    const requestSerializer = this.getRequestSerializer();
    const queryString = requestSerializer.serialize({
      ...currentQueryState,
      size,
    });
    const params = Qs.parse(queryString);
    params["page"] = 1;
    params["format"] = format;

    const exportUrl = `${documentApi.searchBaseURL}?${Qs.stringify(params, {
      arrayFormat: "repeat",
      encode: false,
    })}`;

    window.open(exportUrl, "_blank");
  };

  handleMARC21Export = async (currentQueryState, size, format) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["format"] = format;

      const exportUrl = `${documentApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;

      const response = await fetch(exportUrl, {
        credentials: "include",
        headers: {
          Accept:
            format === "marcxml"
              ? "application/marcxml+xml"
              : "application/marc",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Export failed: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const fileName = this.generateFileName(format, size);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      this.handleStandardExport(currentQueryState, size, format);
    }
  };

  generateFileName = (format, size) => {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, "");
    const sizeStr = size === -1 ? "all" : size.toString();

    switch (format) {
      case "marcxml":
        return `documents_export_${sizeStr}_${timestamp}.xml`;
      case "marc21":
        return `documents_export_${sizeStr}_${timestamp}.mrc`;
      case "csv":
        return `documents_export_${sizeStr}_${timestamp}.csv`;
      case "pdf":
        return `documents_export_${sizeStr}_${timestamp}.pdf`;
      default:
        return `documents_export_${sizeStr}_${timestamp}.${format}`;
    }
  };

  customExportPDF = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params["page"] = 1;
      params["size"] = size;

      // Preserve the sort from currentQueryState, or use default if none specified
      if (!params["sort"] && currentQueryState?.sortBy) {
        params["sort"] = currentQueryState.sortBy;
      } else if (!params["sort"]) {
        params["sort"] = "-created";
      }

      // Fetch JSON data instead of CSV to get all fields properly
      const exportUrl = `${documentApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;

      const response = await fetch(exportUrl, { credentials: "include" });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jsonData = await response.json();
      const hits = jsonData.hits?.hits || [];

      // Build table headers
      const tableHeaders = [
        "Title",
        "Author",
        "Published Year",
        "Material Type",
      ]
        .map((h) => `<th>${h}</th>`)
        .join("");

      // Build table rows from JSON data
      const tableRows = hits
        .map((hit) => {
          const metadata = hit.metadata || {};

          const title = metadata.title || "";
          const authors =
            (metadata.authors || []).map((a) => a.full_name || "").join(", ") ||
            "";
          const year = metadata.publication_year || "";
          const materialType = metadata.document_type || "";

          // Escape HTML to prevent XSS and display issues
          const escapeHtml = (text) => {
            const div = document.createElement("div");
            div.textContent = text;
            return div.innerHTML;
          };

          return `
          <tr>
            <td>${escapeHtml(title)}</td>
            <td>${escapeHtml(authors)}</td>
            <td>${escapeHtml(year)}</td>
            <td>${escapeHtml(materialType)}</td>
          </tr>
        `;
        })
        .join("");

      const htmlContent = generateExportHTML({
        reportTitle: "Documents Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        reportType: "default",
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => printWindow.focus(), 500);
        };
      } else {
        console.error("Failed to open print window - popup blocked?");
        alert("Please allow popups for this site to export to PDF");
      }
    } catch (error) {
      console.error("PDF Export Error:", error);
      alert(`PDF export failed: ${error.message}`);
    }
  };

  render() {
    const helperFields = [
      {
        name: "author",
        field: "authors.full_name",
        defaultValue: '"Doe, John"',
      },
      { name: "created", field: "_created" },
      { name: "published", field: "imprint.date" },
    ];
    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSorting = setReactSearchKitDefaultSortingOnEmptyQueryString(
      this.modelName
    );
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">Bibliographic Records</Header>
        <OverridableContext.Provider value={{ ...SearchControlsOverridesMap }}>
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSorting}
          >
            <>
              <SessionFiltersPersistence moduleIdentifier="documents" />
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for documents..."
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>
              <Container fluid className="bo-search-body">
                <Grid>
                  <Grid.Row columns={2}>
                    <ResultsLoader>
                      <Grid.Column width={3} className="search-aggregations">
                        <Grid.Column width={16}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                            }}
                          >
                            <Header
                              as="h4"
                              content="Filter by"
                              style={{ margin: 0 }}
                            />
                            <ClearFiltersButton module-identifier="documents" />
                          </div>
                        </Grid.Column>
                        <SearchAggregationsCards modelName={this.modelName} />
                        <SearchDateRange
                          title="Copyright Year"
                          fromDateFilter="copyrights.year_from"
                          toDateFilter="copyrights.year_to"
                          yearOnly
                        />
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={16} textAlign="right">
                              <WeedingThresholdButton />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                        <Grid>
                          <Grid.Column width={3}>
                            <NewButton
                              text="Add Document"
                              to={BackOfficeRoutes.documentCreate}
                              style={{ width: "100%", textAlign: "left" }}
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <MARC21ImportUploader
                              trigger={
                                <Button
                                  fluid
                                  color="blue"
                                  icon="upload"
                                  content="Import MARC21"
                                  style={{ padding: "9.5px" }}
                                />
                              }
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <ExcelImportUploader
                              trigger={
                                <Button
                                  fluid
                                  color="blue"
                                  icon="file excel"
                                  content="Import Excel"
                                  style={{ padding: "9.5px" }}
                                />
                              }
                              onImportComplete={this.handleImportComplete}
                            />
                          </Grid.Column>
                          <Grid.Column width={4}>
                            <ExportReactSearchKitResults
                              exportBaseUrl={documentApi.searchBaseURL}
                              onExportClick={this.handleExportClick}
                            />
                          </Grid.Column>
                          <Grid.Column width={1} textAlign="center">
                            <Popup
                              content="See weeded documents list"
                              position="right center"
                              trigger={
                                <Button
                                  className="back-to-documents"
                                  primary
                                  basic
                                  as={Link}
                                  to={BackOfficeRoutes.weededDocuments}
                                  icon="arrow right"
                                />
                              }
                            />
                          </Grid.Column>
                        </Grid>
                        <EmptyResults
                          extraContent={
                            <NewButton
                              text="Add Document"
                              to={BackOfficeRoutes.documentCreate}
                              style={{ width: "100%", textAlign: "left" }}
                            />
                          }
                        />
                        <Error />
                        <SearchControls
                          modelName={this.modelName}
                          withLayoutSwitcher={false}
                        />
                        <ResultsList ListEntryElement={DocumentListEntry} />
                        <SearchFooter />
                      </Grid.Column>
                    </ResultsLoader>
                  </Grid.Row>
                </Grid>
              </Container>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
